import { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import moment from 'moment';
import { hasValue } from '@/utils/legacy-utils';

// ============================================================================
// External Declarations
// ============================================================================

import { sendChatMessage as apiSendChatMessage, getChatHistory as apiGetChatHistory } from '@/services/api';
import { getCurrentCompanyId, getCurrentUserId } from '@/utils/auth';

// ============================================================================
// Constants & Types
// ============================================================================

const DATETIME_FORMAT_COMPANY_CHAT_MOMENT = 'DD MMM HH:mm:ss';

enum ChatType {
  All = 1,
  MDT = 2,
  CAD = 3,
}

interface ChatMessage {
  timetoken: number;
  personId: number;
  personName: string;
  chatType: number;
  entry: string;
  isSelf: boolean;
}

// ============================================================================
// Module-level State
// ============================================================================

let currentPersonName: string | null = null;
let hasReceivedMessageHistory = false;
let storedMessages: ChatMessage[] = [];

// Callbacks registered by the mounted React component
let addMessageCallback: ((msg: ChatMessage) => void) | null = null;
let setAllMessagesCallback: ((msgs: ChatMessage[]) => void) | null = null;
let scrollToBottomCallback: (() => void) | null = null;

// ============================================================================
// MessagingScreen Component
// ============================================================================

export function MessagingScreen() {
  const { theme } = useTheme();

  const [messages, setMessages] = useState<ChatMessage[]>(storedMessages);
  const [newMessage, setNewMessage] = useState('');
  const chatMessagesRef = useRef<HTMLDivElement>(null);

  // --- Scroll to bottom ---
  const scrollToBottom = useCallback(() => {
    const el = chatMessagesRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, []);

  // Register callbacks for external code (PubNub, history loading)
  useEffect(() => {
    addMessageCallback = (msg: ChatMessage) => {
      setMessages((prev) => {
        const next = [...prev, msg];
        storedMessages = next;
        return next;
      });
    };
    setAllMessagesCallback = (newMsgs: ChatMessage[]) => {
      storedMessages = newMsgs;
      setMessages(newMsgs);
    };
    scrollToBottomCallback = scrollToBottom;

    return () => {
      addMessageCallback = null;
      setAllMessagesCallback = null;
      scrollToBottomCallback = null;
    };
  }, [scrollToBottom]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // --- Init company chat on mount ---
  useEffect(() => {
    resetBadge();
    CompanyChatReact.setShown(true);
    initCompanyChat();
    return () => {
      CompanyChatReact.setShown(false);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Helpers ---
  function getInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    return (
      parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
    ).toUpperCase();
  }

  function formatTime(timetoken: number): string {
    const timestamp = Math.floor(timetoken * 0.0000001);
    return moment.unix(timestamp).format(DATETIME_FORMAT_COMPANY_CHAT_MOMENT);
  }

  // --- Send message ---
  const sendMessage = useCallback(() => {
    const entry = newMessage.trim();
    if (!entry) return;

    setNewMessage('');

    const doSend = (personName: string) => {
      const userId = getCurrentUserId();
      const curTimeToken = moment().unix() * 10000000;

      const chatMessage: ChatMessage = {
        timetoken: curTimeToken,
        personId: userId,
        personName: personName,
        chatType: ChatType.All,
        entry: entry,
        isSelf: true,
      };

      setMessages((prev) => {
        const next = [...prev, chatMessage];
        storedMessages = next;
        return next;
      });

      apiSendChatMessage(entry, ChatType.All).catch((err) => {
        console.error('Failed to send message:', err);
      });
    };

    if (currentPersonName) {
      doSend(currentPersonName);
    } else {
      api.getUserName((result: any) => {
        currentPersonName = result.name;
        doSend(currentPersonName!);
      });
    }
  }, [newMessage]);

  // --- Render ---
  return (
    <div id="MessagingSection" className={theme}>
      <div className="kst-page-container">
        <div className="kst-layout kst-layout--no-sidebar">
          <div className="kst-main-content">
            <div className="kst-chat-container">
              {/* Chat Header */}
              <div className="kst-chat-header">
                <h3 className="kst-chat-title">Company Chat</h3>
              </div>

              {/* Chat Messages Area */}
              <div className="kst-chat-messages" ref={chatMessagesRef}>
                {messages.length === 0 ? (
                  <div className="kst-chat-empty">
                    No messages yet. Start the conversation!
                  </div>
                ) : (
                  messages.map((message) => (
                    <div
                      key={message.timetoken}
                      className={`kst-message${message.isSelf ? ' kst-message--self' : ''}`}
                    >
                      <div
                        className={`kst-message-avatar user${message.personId}`}
                      >
                        {getInitials(message.personName)}
                      </div>
                      <div className="kst-message-content">
                        <div className="kst-message-header">
                          <span className="kst-message-name">
                            {message.isSelf ? 'You' : message.personName}
                          </span>
                          <span className="kst-message-time">
                            {formatTime(message.timetoken)}
                          </span>
                        </div>
                        <div className="kst-message-bubble">{message.entry}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Chat Input Form */}
              <div className="kst-chat-form">
                <input
                  type="text"
                  className="kst-chat-input"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyUp={(e) => {
                    if (e.key === 'Enter') sendMessage();
                  }}
                  placeholder="Type your message..."
                />
                <button
                  type="button"
                  className="kstc-btn kstc-btn--primary kst-chat-send-btn"
                  onClick={sendMessage}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M22 2L11 13"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M22 2L15 22L11 13L2 9L22 2Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>Send</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Module-level Functions (kept outside the component)
// ============================================================================

function preloadUserImages(): void {
  api.getMessagingUserInfoList((result: any) => {
    App.profileImages = result.infoList;

    const existingStyle = document.getElementById('msgImgStyles');
    if (existingStyle) {
      existingStyle.remove();
    }

    let styleContent = '';
    for (let i = 0; i < App.profileImages.length; i++) {
      const person = App.profileImages[i];
      styleContent += `.user${person.personId} { content:url('${person['personPic']}') !important; background: url('${person['personPic']}') !important; background-size: cover !important; color: transparent !important; } `;
    }

    if (styleContent) {
      const style = document.createElement('style');
      style.id = 'msgImgStyles';
      style.textContent = styleContent;
      document.head.appendChild(style);
    }
  });
}

function loadCurrentPersonName(): void {
  api.getUserName((result: any) => {
    currentPersonName = result.name;
  });
}

function initCompanyChat(): void {
  preloadUserImages();

  if (hasReceivedMessageHistory) {
    // Messages already stored, just scroll to bottom
    setTimeout(() => {
      if (scrollToBottomCallback) scrollToBottomCallback();
    }, 0);
  } else {
    App.initPubNub(() => {
      apiGetChatHistory().then((response: any) => {
        hasReceivedMessageHistory = true;
        if (!response || !response.messages) return;
        const userId = getCurrentUserId();
        const newMessages: ChatMessage[] = [];

        for (const message of response.messages) {
          newMessages.push({
            timetoken: new Date(message.dateCreated).getTime() * 10000,
            personId: message.personId,
            personName: message.personName,
            chatType: message.chatType,
            entry: message.entry,
            isSelf: userId === message.personId,
          });
        }

        // Messages come from REST API already sorted ascending
        storedMessages = newMessages;

        // Update React component if mounted
        if (setAllMessagesCallback) {
          setAllMessagesCallback(newMessages);
        }
      });
    });
  }

  // Get current user's name
  loadCurrentPersonName();
}

function resetBadge(): void {
  App.unReadMessagesCnt = 0;
  updateBadgeElements();
  syncUnreadCountToApp();
}

function updateBadgeElements(): void {
  const badges = document.querySelectorAll("[id='msgCount']");
  badges.forEach((badge) => {
    const el = badge as HTMLElement;
    el.textContent = App.unReadMessagesCnt.toString();
    el.style.display = App.unReadMessagesCnt === 0 ? 'none' : '';
  });
}

// A mounted Messaging screen only means the messages were read if the window
// is actually visible. Clear the unread count when the window regains focus
// while the Messaging screen is showing — the messages are on screen then.
function clearUnreadIfMessagingVisible(): void {
  if (addMessageCallback && !document.hidden && document.hasFocus() && App.unReadMessagesCnt > 0) {
    resetBadge();
  }
}
window.addEventListener('focus', clearUnreadIfMessagingVisible);
document.addEventListener('visibilitychange', clearUnreadIfMessagingVisible);

// Mirror the unread count to the Windows shell so it can show a taskbar badge.
// Gated to the Windows app only — browsers and the iOS/Android wrappers never
// receive this message. Shells that predate it (<= 2.0.6) ignore unknown types.
function syncUnreadCountToApp(): void {
  if (!(window as any).isWindowsAppSdkApp) return;
  (window as any).notifyApp?.('unreadMessageCount', [App.unReadMessagesCnt.toString()]);
}

function receiveCompanyChatMessage(message: any, timetoken: number): void {
  const userId = getCurrentUserId();

  // Don't process messages from self (already added when sending)
  if (userId === message.personId) {
    return;
  }

  if (addMessageCallback) {
    // Messaging screen is mounted - add to the list
    const chatMessage: ChatMessage = {
      timetoken: timetoken,
      personId: message.personId,
      personName: message.personName,
      chatType: message.chatType,
      entry: message.entry,
      isSelf: false,
    };
    addMessageCallback(chatMessage);

    // The screen being mounted doesn't mean anyone saw the message: with the
    // window minimized or unfocused, count it as unread (taskbar badge in the
    // Windows app, toolbar badge otherwise) and play the chat sound. The count
    // clears via clearUnreadIfMessagingVisible when the window regains focus.
    if (document.hidden || !document.hasFocus()) {
      App.unReadMessagesCnt += 1;
      updateBadgeElements();
      syncUnreadCountToApp();

      if (hasValue(App.sndChat)) {
        App.sndChat.play().catch(() => {});
      }
    }
  } else {
    // Not on the messaging screen - show notification
    const notificationMessage = `${message.personName}: ${message.entry}`;
    App.unReadMessagesCnt += 1;
    updateBadgeElements();
    syncUnreadCountToApp();

    if (hasValue(App.sndChat)) {
      App.sndChat.play().catch(() => {});
    }

    App.showPopupNotification(notificationMessage, 'success');
  }
}

function chatCleanup(): void {
  currentPersonName = null;
  hasReceivedMessageHistory = false;
  storedMessages = [];
  CompanyChatReact.setShown(false);
}

// ============================================================================
// CompanyChat Compatibility Class
// ============================================================================

class CompanyChatReact {
  private static _isShown = false;

  static handlePresenceEvent(_presenceEvent: any): void {
    // Presence events can be handled here if needed
  }

  static chatCleanup(): void {
    chatCleanup();
  }

  static setShown(isShown: boolean): void {
    CompanyChatReact._isShown = isShown;
  }

  static isShown(): boolean {
    return CompanyChatReact._isShown;
  }

  static initCompanyChat(): void {
    initCompanyChat();
  }

  static receiveCompanyChatMessage(message: any, timetoken: number): void {
    receiveCompanyChatMessage(message, timetoken);
  }

  static sendEntry(): void {
    // Send is handled internally by the React component.
    // This static method is kept for backward compatibility.
  }

  static scrollToBottom(): void {
    if (scrollToBottomCallback) scrollToBottomCallback();
  }
}

// ============================================================================
// Window Registration
// ============================================================================

(window as any).CompanyChatReact = CompanyChatReact;
(window as any).CompanyChat = CompanyChatReact;
(window as any).messagingAdmin = {
  resetBadge,
  chatCleanup,
  scrollToBottom: () => { if (scrollToBottomCallback) scrollToBottomCallback(); },
  receiveCompanyChatMessage,
  sendMessage: () => {},
  preloadUserImages,
  initCompanyChat,
};

// ============================================================================
// Screen Registry
// ============================================================================

// ============================================================================
// Exports
// ============================================================================

export {
  CompanyChatReact,
  resetBadge,
  receiveCompanyChatMessage,
  chatCleanup,
};
